import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./Results.css";
import ResultCard from "./ResultCard";
import DiscoverFields from "./DiscoverFields";
import SearchBar from "./SearchBar";
import { useAuth } from "../../auth/AuthContext";
import { apiRequest } from "../../api/client";
import { defaultDiscoveryPreferences, DiscoveryPreferences } from "./discoveryOptions";

const STORAGE_KEY = "find-a-film:discovery";
type SavedDiscovery = { preferences: DiscoveryPreferences; recentSearches: string[] };

const readSavedDiscovery = (): SavedDiscovery => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (saved?.preferences) return { preferences: { ...defaultDiscoveryPreferences(), ...saved.preferences }, recentSearches: Array.isArray(saved.recentSearches) ? saved.recentSearches.slice(0, 6) : [] };
  } catch { /* Ignore malformed legacy storage. */ }
  return { preferences: defaultDiscoveryPreferences(), recentSearches: [] };
};

const Results = () => {
  const [results, setResults] = useState<any[]>([]);
  const [msg, setMsg] = useState("");
  const [isSortedYear, setIsSortedYear] = useState(false);
  const [isSortedRating, setIsSortedRating] = useState(false);
  const [prevResults, setPrevResults] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const initialQuery = params.get("q") || "";
  const [mode, setMode] = useState<"title" | "discover">(initialQuery ? "title" : "discover");
  const [savedDiscovery, setSavedDiscovery] = useState<SavedDiscovery>(readSavedDiscovery);
  const [saveState, setSaveState] = useState("Saved on this device");
  const { user } = useAuth();

  useEffect(() => {
    if (!user) { setSaveState("Saved on this device"); return; }
    let active = true;
    apiRequest<any>("/me").then(profile => {
      if (!active) return;
      if (profile?.discoveryPreferences) {
        const merged = { preferences: { ...defaultDiscoveryPreferences(), ...profile.discoveryPreferences }, recentSearches: Array.isArray(profile.recentSearches) ? profile.recentSearches.slice(0, 6) : savedDiscovery.recentSearches };
        setSavedDiscovery(merged);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      }
      setSaveState("Synced to your account");
    }).catch(() => setSaveState("Saved on this device"));
    return () => { active = false; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const remember = (next: SavedDiscovery) => {
    setSavedDiscovery(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    if (user) {
      setSaveState("Saving…");
      apiRequest("/me", { method: "PUT", body: JSON.stringify({ discoveryPreferences: next.preferences, recentSearches: next.recentSearches }) })
        .then(() => setSaveState("Synced to your account"))
        .catch(() => setSaveState("Saved on this device"));
    }
  };

  const recordSearch = (query: string) => {
    const recentSearches = [query, ...savedDiscovery.recentSearches.filter(item => item.toLowerCase() !== query.toLowerCase())].slice(0, 6);
    remember({ ...savedDiscovery, recentSearches });
  };

  const sortByReleaseYear = () => {
    if (isSortedYear === false) {
      setPrevResults(results);
      const sortedResults = [...results].sort(function (o1: any, o2: any) {
        if (o1.release_date > o2.release_date) {
          return -1;
        } else if (o1.release_date < o2.release_date) {
          return 1;
        } else {
          return 0;
        }
      });
      setResults(sortedResults);
      setIsSortedYear(true);
      setIsSortedRating(false);
    } else {
      setIsSortedYear(false);
      setResults(prevResults);
    }
  };

  const sortByRating = () => {
    if (isSortedRating === false) {
      setPrevResults(results);
      const sortedResultsRating = [...results].sort(function (
        o1: any,
        o2: any
      ) {
        if (o1.vote_average > o2.vote_average) {
          return -1;
        } else if (o1.vote_average < o2.vote_average) {
          return 1;
        } else {
          return 0;
        }
      });
      setResults(sortedResultsRating);
      setIsSortedRating(true);
      setIsSortedYear(false);
    } else {
      setIsSortedRating(false);
      setResults(prevResults);
    }
  };

  return (
    <section className="search" id="discover" aria-labelledby="discover-heading">
      <div className="search__panel">
        <div className="search__heading"><div><h2 id="discover-heading">What would you like to watch?</h2></div><span className="search__save-state"><span aria-hidden="true">✓</span>{saveState}</span></div>
        <div className="search__modes" role="tablist" aria-label="Ways to find a film">
          <button type="button" role="tab" aria-selected={mode === "discover"} className={mode === "discover" ? "is-active" : ""} onClick={() => setMode("discover")}><span aria-hidden="true">✦</span><span><strong>Recommend something</strong><small>Choose a mood and services</small></span></button>
          <button type="button" role="tab" aria-selected={mode === "title"} className={mode === "title" ? "is-active" : ""} onClick={() => setMode("title")}><span aria-hidden="true">⌕</span><span><strong>Search by title</strong><small>Find a film you already know</small></span></button>
        </div>
        <div className="search__mode-content" role="tabpanel">
          {mode === "title" ? <SearchBar setResults={setResults} setMsg={setMsg} setIsSortedYear={setIsSortedYear} setIsSortedRating={setIsSortedRating} setLoading={setLoading} recentSearches={savedDiscovery.recentSearches} onSearchRecorded={recordSearch} initialQuery={initialQuery}/> : <DiscoverFields setResults={setResults} setMsg={setMsg} setLoading={setLoading} preferences={savedDiscovery.preferences} setPreferences={preferences => setSavedDiscovery({ ...savedDiscovery, preferences })} onPreferencesUsed={preferences => remember({ ...savedDiscovery, preferences })} />}
        </div>
      </div>
      <div className="results__topline">
        <h3 className="results__message" aria-live="polite">{loading ? "Finding great films…" : msg}</h3>
      {results.length > 0 && (
        <div className="sort__options">
          <span>Sort:</span>
          <button className="sort__button" onClick={sortByReleaseYear}>
            {isSortedYear ? "Relevance" : "Release year"}
          </button>
          <button className="sort__button" onClick={sortByRating}>
            {isSortedRating ? "Relevance" : "Rating"}
          </button>
        </div>
      )}</div>
      <div className="results__container">
        {results
          .filter((item: any) => {
            if (item.poster_path !== null && (!item.release_date || new Date(item.release_date) <= new Date())) {
              return item;
            }
            return null;
          })
          .map((result: any) => (
            <ResultCard key={result.id} {...result} />
          ))}
      </div>
    </section>
  );
};

export default Results;
